// ============================================================
// CaseSectionNav.tsx
// The case page's GROUPED left section-nav (Stage 1 shell — approved mockup
// "CA132B"): the shared Settings/Admin/Client SectionNavItem (raised-card
// active highlight + primary bar) under group headings, each row carrying the
// setup-chain status mark; locked rows are greyed + non-clickable with the
// "Complete X first" reason. Below md it collapses to the shared scrolling
// SectionNavPill bar. Grouping = CASE_NAV_GROUPS (display only); WHICH rows
// show is decided by the caller (the same visibleHeadings filter as before).
// COMPACT (desktop): an icon rail, on EVERY case section by the shared fold
// rule (case/caseNavFold — auto below ~1200px of content, else the user's
// remembered choice). Each icon is a labelled button: the section name (and
// its status / lock reason) as aria-label + the app's RailTooltip on hover AND
// keyboard focus, with the section's status mark (tick / half / lock / "!")
// as a small badge on the icon. The chevron at the bottom expands the menu;
// "Fold menu" in the full menu folds it.
// ============================================================
import React from 'react'
import { SectionNavItem, SectionNavPill, SIcon } from '../settings/settingsUi'
import { CASE_NAV_GROUPS, CASE_NAV_INDEX, type CaseNavGroup } from '../../constants/caseNav.constants'
import type { CaseSectionKey } from '../../types/case.types'
import type { SectionStatus } from '../../utils/sectionFlow'
import { CaseStatusMark } from './caseUi'
import RailTooltip from '../shared/RailTooltip'

// The statuses shown as a badge on a folded icon (not 'empty' / 'neutral' —
// a grey ring on every untouched section would be noise).
const BADGED = new Set<SectionStatus>(['complete', 'in_progress', 'locked', 'attention'])
const STATUS_WORD: Partial<Record<SectionStatus, string>> = { complete: 'complete', in_progress: 'in progress', locked: 'locked', attention: 'needs attention' }

export interface CaseSectionNavProps {
  // Visible keys in VIEWING_PANE_HEADINGS order (toggles / admin / history
  // gates already applied). Overview is always added by the nav.
  visibleKeys: string[]
  active: CaseSectionKey | null
  statusByKey: Partial<Record<string, SectionStatus>>
  lockReasonByKey: Partial<Record<string, string>>
  subByKey: Partial<Record<string, React.ReactNode>>
  hiddenCount: number
  onSelect: (key: CaseSectionKey) => void
  onManageSections: () => void
  // Icon-rail mode (desktop) + the toggle between rail and full menu.
  compact?: boolean
  onToggleCompact?: () => void
}

// Groups filtered to what's visible; any visible key not in the table falls
// into an "Other" group so a new heading can never be orphaned.
const visibleGroups = (visibleKeys: string[]): CaseNavGroup[] => {
  const vis = new Set<string>(['overview', ...visibleKeys])
  const groups = CASE_NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => vis.has(it.key)) }))
    .filter((g) => g.items.length > 0)
  const orphans = visibleKeys.filter((k) => !CASE_NAV_INDEX[k])
  if (orphans.length > 0) {
    groups.push({ label: 'Other', items: orphans.map((k) => ({ key: k as CaseSectionKey, label: k, icon: 'list' as const })) })
  }
  return groups
}

const CaseSectionNav: React.FC<CaseSectionNavProps> = ({
  visibleKeys, active, statusByKey, lockReasonByKey, subByKey, hiddenCount, onSelect, onManageSections, compact = false, onToggleCompact,
}) => {
  const groups = visibleGroups(visibleKeys)
  return (
    <>
      {/* ── desktop, compact: the icon rail ── */}
      {compact && (
        <nav aria-label="Case sections" className="hidden md:flex md:sticky md:top-4 self-start flex-col items-center gap-[2px] w-[52px] py-[8px] rounded-[14px] border border-casevo-border bg-casevo-surface shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]">
          {groups.map((g, gi) => (
            <React.Fragment key={g.label ?? `g${gi}`}>
              {gi > 0 && <span aria-hidden="true" className="my-[5px] w-[26px] border-t border-casevo-border-soft" />}
              {g.items.map((it) => {
                const status = statusByKey[it.key] ?? 'neutral'
                const locked = status === 'locked'
                const on = active === it.key
                const reason = locked ? lockReasonByKey[it.key] : undefined
                const word = STATUS_WORD[status]
                const name = `${it.label}${word ? ` — ${word}` : ''}${reason ? ` (${reason})` : ''}`
                return (
                  <RailTooltip key={it.key} enabled label={it.label} sublabel={reason ?? (word ? word.charAt(0).toUpperCase() + word.slice(1) : undefined)}>
                    <button type="button" aria-label={name} aria-current={on ? 'page' : undefined} aria-disabled={locked || undefined}
                      onClick={locked ? undefined : () => onSelect(it.key)}
                      className={`relative w-[38px] h-[36px] rounded-[9px] grid place-items-center transition-colors focus:outline-none casevo-focus ${on ? 'bg-casevo-mint text-casevo-link' : locked ? 'text-casevo-text-soft/50 cursor-not-allowed' : 'text-casevo-text-soft hover:bg-casevo-mint/60 hover:text-casevo-forest'}`}>
                      <SIcon name={it.icon} size={17} />
                      {BADGED.has(status) && (
                        <span aria-hidden="true" data-testid={`case-nav-badge-${it.key}`} className="absolute top-[2px] right-[2px] grid place-items-center rounded-full bg-casevo-surface ring-2 ring-casevo-surface leading-none">
                          <CaseStatusMark status={status} size={12} />
                        </span>
                      )}
                    </button>
                  </RailTooltip>
                )
              })}
            </React.Fragment>
          ))}
          {onToggleCompact && (
            <>
              <span aria-hidden="true" className="my-[5px] w-[26px] border-t border-casevo-border-soft" />
              <RailTooltip enabled label="Expand the case menu">
                <button type="button" onClick={onToggleCompact} aria-label="Expand the case menu"
                  className="w-[38px] h-[32px] rounded-[9px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint/60 hover:text-casevo-forest focus:outline-none casevo-focus">
                  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              </RailTooltip>
            </>
          )}
        </nav>
      )}
      {/* ── desktop: grouped nav (mockup .snav) ── */}
      {!compact && <nav aria-label="Case sections" className="leading-[normal] hidden md:block md:sticky md:top-4 self-start">
        {groups.map((g, gi) => (
          <div key={g.label ?? `g${gi}`}>
            {g.label && (
              <div className={`text-[10.5px] tracking-[0.16em] uppercase text-casevo-text-soft font-bold mx-[12px] mb-[6px] ${gi === 0 ? 'mt-0' : 'mt-[16px]'}`}>
                {g.label}
              </div>
            )}
            {g.items.map((it) => {
              const status = statusByKey[it.key] ?? 'neutral'
              const locked = status === 'locked'
              return (
                <SectionNavItem
                  key={it.key}
                  dense
                  icon={it.icon}
                  label={it.label}
                  active={active === it.key}
                  onClick={() => onSelect(it.key)}
                  sub={subByKey[it.key]}
                  trailing={<CaseStatusMark status={status} />}
                  disabled={locked}
                  title={locked ? lockReasonByKey[it.key] : undefined}
                />
              )
            })}
          </div>
        ))}
        <div className="mx-[12px] mt-[14px] pt-[12px] border-t border-casevo-border-soft text-[12px] text-casevo-text-soft leading-[1.45]">
          Ticks follow the setup chain. Locked sections open once the one before is complete.
          <br />
          <button type="button" onClick={onManageSections}
            className="font-semibold text-casevo-link hover:underline focus:outline-none casevo-focus rounded-[3px]">
            Manage sections
          </button>
          {' '}· {hiddenCount} hidden
          {onToggleCompact && (
            <>
              <br />
              <button type="button" onClick={onToggleCompact}
                className="mt-[4px] font-semibold text-casevo-link hover:underline focus:outline-none casevo-focus rounded-[3px]">
                ‹ Fold menu
              </button>
            </>
          )}
        </div>
      </nav>}

      {/* ── mobile: scrolling pill bar (shared SectionNavPill) ── */}
      <div className="md:hidden flex gap-2 overflow-x-auto -mx-4 px-4 pb-1" role="navigation" aria-label="Case sections">
        {groups.flatMap((g) => g.items).map((it) => {
          const status = statusByKey[it.key] ?? 'neutral'
          const locked = status === 'locked'
          return (
            <SectionNavPill
              key={it.key}
              icon={it.icon}
              label={it.label}
              active={active === it.key}
              onClick={() => onSelect(it.key)}
              trailing={status === 'neutral' ? undefined : <CaseStatusMark status={status} size={13} />}
              disabled={locked}
              title={locked ? lockReasonByKey[it.key] : undefined}
            />
          )
        })}
        <button type="button" onClick={onManageSections}
          className="inline-flex items-center text-[13px] px-[12px] py-[7px] rounded-full border border-dashed border-casevo-border text-casevo-text-soft whitespace-nowrap focus:outline-none casevo-focus">
          Manage sections
        </button>
      </div>
    </>
  )
}

export default CaseSectionNav
